import React, { useState, useEffect } from 'react';
import { TextField, Button, Typography, Grid, Box, IconButton } from '@mui/material';
import { PrivateUserInfoRequestModel, UserInfosModel } from '../../../../models/user/UserInfosModel';
import { useTranslationContext } from '../../../../hooks/translations/translationsProvider';
import TextFieldCustom from '../../../TextField/TextFieldCustom';
import DatePickerCustom from '../../../DatePicker/DatePickerCustom';
import dayjs, { Dayjs } from 'dayjs';
import lastDate18YearsAgo from '../../../../utilities/lastDate18YearsAgo';
import { BLUE_DARK, GRAY_LIGHT, GRAY_MAIN, WHITE, GREEN_LOGO, GREEN_MAIN } from '../../../../theme/colors';
import { getAssociationStatusLabel } from '../../../../helpers/approverFromAssociationHelper';
import { useWaiter } from '../../../../hooks/waiter/WaiterProvider';
import { Snackbar, Alert } from '@mui/material';
import SearchNations from '../../../UserInfo/searchNation';
import AlertSaveModal from '../../../UserInfo/AlertSaveModal';
import { Close as CloseIcon } from '@mui/icons-material';
import AssociationApproval from './AssociationApproval';
import EnableUser from './EnableUser';
import NationalEntityCheckbox from './NationalEntityCheckbox';
import { putAdminUserPrivate } from '../../../../hooks/actions/admin';
import SearchAssociations from '../../../AssociationsList/searchAssociation';
import { extractHttpErrorMessage } from '../../../../helpers/extractHttpErrorMessage';
import { ESex } from '../../../../hooks/auth/models';
import SexSelect from '../../../UserInfo/DropDownSex';
import { useUserContext } from '../../../../hooks/auth/UserProvider';
import { isDevSuperAdminUser } from '../../../../helpers/authorizedUser';

interface PrivateProps {
  user: UserInfosModel;
  onBack: () => void;
}

type AdminPrivateUserFormData = PrivateUserInfoRequestModel;

const AdminPrivateUser: React.FC<PrivateProps> = ({user, onBack}) => {  
  const { t } = useTranslationContext();
  const { user: adminUser } = useUserContext();
  const canManageNationalEntity = isDevSuperAdminUser(adminUser);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<AdminPrivateUserFormData>({
    userId: user.userId || '', // ID dell'utente privato
    isPrivate: user.isPrivate,
    conventioned: user.conventioned,
    email: user.email,
    enabledUser: user.enabledUser === true,
    username: user.username,
    userType: user.userType,
    refCode: user.refCode || '',
    association: user.association || '',
    lastDateAssociationAction: user.lastDateAssociationAction || undefined,
    removedFromAssociation: user.removedFromAssociation || undefined,
    approvedFromAssociation: Number(user.approvedFromAssociation) || 0,

    // Dati specifici dell'utente privato (PrivateInfos)
    userInfoId: user.privateInfo?.id || '',
    nome: user.privateInfo?.nome || '',
    cognome: user.privateInfo?.cognome || '',
    sex: user.privateInfo?.sex || ESex.Male,
    born: user.privateInfo?.born || new Date(),
    address: user.privateInfo?.address || '',
    comuneDiNascita: user.privateInfo?.comuneDiNascita || '',
    statoDiNascita: user.privateInfo?.statoDiNascita || '',
    city: user.privateInfo?.city || '',
    cap: user.privateInfo?.cap || 0,
    provincia: user.privateInfo?.provincia || '',
    nation: user.privateInfo?.nation || '',
    codiceFiscale: user.privateInfo?.codiceFiscale || '',
    professione: user.privateInfo?.professione || '',
    telephone: user.telephone || '',
    isNationalReference: user.isNationalReference === true,
  });
  const [showPopup, setShowPopup] = useState(false);
  const {setWaiterView} = useWaiter();
  const [initialFormData, setInitialFormData] = useState({ ...formData }); // Salva i dati iniziali

  const [modalOpen, setModalOpen] = useState(false);
  const bornDate = initialFormData.born ? dayjs(initialFormData.born) : dayjs("01-01-1900")
  const [selectedDate, setSelectedDate] = useState<Dayjs | undefined>(bornDate); 
  const [bornError, setBornError] = useState(false);
  const [bornErrorMessage, setBornErrorMessage] = useState('');
  const [maxData] = useState<Dayjs | undefined>(dayjs(lastDate18YearsAgo()));

  const [mainError, setMainError] = useState<boolean>(false);
  const [mainErrorMex, setMainErrorMex] = useState<string>("");

  const handleChange = (value: string, key: string) => {
    if(key === 'enabledUser' || key === 'isNationalReference'){
      const boolVal = value === 'false' ? false : true;
      setFormData((prev) => ({
        ...prev,
        [key]: boolVal, // Directly update with the string value
      }));
    }else{
      setFormData((prev) => ({
        ...prev,
        [key]: value, // Directly update with the string value
      }));
    }
  };

  useEffect(()=>{
  },[formData])

  const handleEdit = () => {
    setMainError(false); // cancello eventuali errori
    setMainErrorMex("");

    setIsEditing(true);
  };

  const handleSave = () => {
    setMainError(false); // cancello eventuali errori
    setMainErrorMex("");

    handleCloseModal(); // chiudo il modal

    if(JSON.stringify(formData) === JSON.stringify(initialFormData)){ // verifico che i dati non siano gli stessi
      setIsEditing(false);

      setShowPopup(true); // apertura e chiusura del popup
      setTimeout(() => {
        setShowPopup(false);
      }, 10000);

      return;
    }
    setWaiterView(true);

    putAdminUserPrivate({
      ...formData,
      isNationalReference: canManageNationalEntity
        ? formData.isNationalReference
        : initialFormData.isNationalReference,
    }).then((resp) => {
      const updatedFormData: AdminPrivateUserFormData = {
        ...formData,
        ...resp,
        isNationalReference: resp.isNationalReference ?? formData.isNationalReference,
      };
      setFormData(updatedFormData);
      setInitialFormData(updatedFormData);
      setWaiterView(false);
      setIsEditing(false);

      setShowPopup(true);
      setTimeout(() => {
        setShowPopup(false);
      }, 10000);

    }).catch((error) => {
      setMainError(true);
      setMainErrorMex(extractHttpErrorMessage(error, t("Errore durante la modifica dei dati")));
      setWaiterView(false);
    });
  };

  const handleCancel = () => {
    setMainError(false); // cancello eventuali errori
    setMainErrorMex("");

    setFormData(initialFormData); // Reset dei dati al valore iniziale
    setIsEditing(false);
  };

  // Conditionally render the component output
  if (!user.userId) {
    return <div>{t("Utente privato non disponibile.")}</div>;
  }

  const handleChangeDate = (date: Dayjs | null) => {
    setBornErrorMessage('')
    if(date !== null && date.isValid()){ // se la data è valida mi setti false, ma se per caso dovesse essere meno di 18 anni fai true all'errore
        setSelectedDate(date)
        setBornError(false)

        if(maxData !== undefined && date > maxData){
            setBornErrorMessage(t("Devi avere più di 18 anni per iscriverti"))
            setBornError(true)
        } 
        // 🔧 Normalizza la data in formato UTC
        const normalized = new Date(Date.UTC(date.year(), date.month(), date.date()));
        setFormData((prev) => ({
          ...prev,
          ['born']: normalized, // ora sarà "1977-02-01T00:00:00.000Z"
        }));
    } 
    if (date !== null && !date.isValid()){
        setBornErrorMessage(t("Data non valida"))
        setBornError(true)
    }
  };

  const handleChangeAssociation = (association: string, associationId: string) => {
    setFormData((prev) => ({
      ...prev,
      ['association']: association,      // Aggiorna il campo 'association' con il nuovo valore
      ['associationId']: associationId,    // Aggiorna il campo 'associationId' con il nuovo valore
    }));
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  return (
    <>
      <div>
        <Box
          sx={{
            display:'flex',
            justifyContent:'flex-end'
          }}
        >
          <IconButton onClick={() => {
            handleCancel(); 
            setIsEditing(false); 
            onBack(); 
          }} 
             sx={{
              backgroundColor: 'white', // Sfondo bianco
              color: GREEN_MAIN, // Colore dell'icona verde
              '&:hover': {
                backgroundColor: 'rgba(128,128,128, 0.1)', // Sfondo rosso chiaro al passaggio del mouse
              },
              borderRadius: '50%', // Per renderlo rotondo
              padding: 1, // Margine interno
            }}
          >
            <CloseIcon sx={{
                fontSize:"40px",
                '&:hover': {
                  fontWeight:'bold'
                },
              }}
            />
          </IconButton>
        </Box>

        <h5 style={{ color: GREEN_MAIN, fontWeight: 'bold', marginBottom: '30px', fontSize: '1.5rem' }}>{t("Utente Privato")}</h5>
        <form>
          <Box justifyItems='center'>
            <Grid container xs={12} md={8} spacing={3} paddingBottom='100px'>
              <Grid item xs={12} sm={6}>
                <TextFieldCustom
                  label={t("Nome")}
                  regex={new RegExp("^[\\p{L}\\p{M} \\-']+$", "u")}
                  value={formData.nome || ''}
                  errorText={t("Richieste lettere e spazi")}
                  onChange={(value) => handleChange(value, 'nome')}
                  disable={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextFieldCustom
                  label={t("Cognome")}
                  regex={new RegExp("^[\\p{L}\\p{M} \\-']+$", "u")}
                  value={formData.cognome || ''}
                  errorText={t("Richieste lettere e spazi")}
                  onChange={(value) => handleChange(value, 'cognome')}
                  disable={!isEditing}
                />
              </Grid>
              <Grid item xs={4} sm={2}>
                <SexSelect 
                  value={formData.sex || ESex.Male}
                  onChange={(value) => handleChange(value, 'sex')}
                  disable={!isEditing}
                />
              </Grid>
              <Grid item xs={8} sm={4}>
                <DatePickerCustom
                  label={t("Data di Nascita")}
                  selectedDate={selectedDate}
                  handleChangeDate={handleChangeDate}
                  maxData={maxData}
                  error={bornError}
                  errorMessage={bornErrorMessage}
                  disable={!isEditing}
                  stackProps={{
                    sx: {
                      marginTop: 0,
                      marginBottom: 0,
                      '& .MuiOutlinedInput-notchedOutline': {
                        color: GREEN_MAIN,
                      },
                      '& .MuiInputLabel-root': {
                        top: -4,
                        fontSize: '1rem',
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: GREEN_MAIN,
                      },
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: GREEN_LOGO,
                        color: GREEN_MAIN,
                      },
                      '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'grey !important',
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '12px 12px',
                        lineHeight: 1.4,
                        color: "grey",
                        fontSize: '1rem',
                      },
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextFieldCustom
                  label={t("Codice Fiscale")}
                  regex={/^[A-Za-z0-9]*$/}
                  value={formData.codiceFiscale || '' }
                  onChange={(value) => handleChange(value, 'codiceFiscale')}
                  errorText={t("Richiesti lettere e numeri")}
                  disable={!isEditing}
                  props={{
                    sx: {
                      '& .MuiOutlinedInput-notchedOutline': {
                        color: GREEN_MAIN,
                      },
                      '& .MuiInputLabel-root': {
                        top: -4,
                        fontSize: '1rem',
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: GREEN_MAIN,
                      },
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: GREEN_LOGO,
                        color: GREEN_MAIN,
                      },
                      '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'grey !important',
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '12px 12px',
                        lineHeight: 1.4,
                        color: "grey",
                        fontSize: '1rem'
                      },
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={7}>
                <TextFieldCustom
                  label={t("Comune di nascita")}
                  regex={new RegExp("^[\\p{L}\\p{M} \\-']+$", "u")}
                  value={formData.comuneDiNascita || ''}
                  onChange={(value) => handleChange(value, 'comuneDiNascita')}
                  errorText={t("Richieste lettere e spazi")}
                  disable={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <SearchNations 
                  label={t("Stato di nascita")}
                  nazione={formData.statoDiNascita || ''} 
                  setNazione={(value) => handleChange(value, 'statoDiNascita')} 
                  disable={!isEditing}
                />
              </Grid>
            </Grid>

            <Grid container xs={12} md={8} spacing={3} paddingBottom='30px'>
              <Grid item xs={12} sm={6}>
                <TextFieldCustom
                  label={t("Indirizzo")}
                  regex={new RegExp("^[\\p{L}\\p{N}\\p{M}\\p{P}\\p{Z}\\p{S} ,\\.\\-\\/]+$", "u")}
                  value={formData.address || ''}
                  onChange={(value) => handleChange(value, 'address')}
                  errorText={t("Richieste solo lettere, spazi, numeri, virgola e meno e /")}
                  disable={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextFieldCustom
                  label={t("Città")}
                  regex={new RegExp("^[\\p{L}\\p{M} \\-']+$", "u")}
                  value={formData.city || ''}
                  onChange={(value) => handleChange(value, 'city')}
                  errorText={t("Richieste lettere e spazi")}
                  disable={!isEditing}
                />
              </Grid>
              <Grid item xs={4} sm={3}>
                <TextFieldCustom
                  label={t("CAP")}
                  regex={/^[0-9]+$/}
                  value={formData.cap === 0 || formData.cap === undefined ? '' : formData.cap.toString()}
                  onChange={(value) => handleChange(value, 'cap')}
                  errorText={t("Richiesti solo Numeri")}
                  disable={!isEditing}
                />
              </Grid>
              <Grid item xs={8} sm={4}>
                <TextFieldCustom
                  label={t("Provincia")}
                  regex={new RegExp("^[\\p{L} \\-']{2,50}$", "u")}
                  value={formData.provincia || '' }
                  onChange={(value) => handleChange(value, 'provincia')}
                  errorText={t("Richieste solo Lettere")}
                  disable={!isEditing}
                  props={{
                    sx: {
                      '& .MuiOutlinedInput-notchedOutline': {
                        color: GREEN_MAIN,
                      },
                      '& .MuiInputLabel-root': {
                        top: -4,
                        fontSize: '1rem',
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: GREEN_MAIN,
                      },
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: GREEN_LOGO,
                        color: GREEN_MAIN,
                      },
                      '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'grey !important',
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '12px 12px',
                        lineHeight: 1.4,
                        color: "grey",
                        fontSize: '1rem'
                      },
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <SearchNations 
                  label={t("Stato")}
                  nazione={formData.nation || ''} 
                  setNazione={(value) => handleChange(value, 'nation')} 
                  disable={!isEditing}
                />
              </Grid>

              <Grid item xs={12} sm={7} md={6}>
                <TextFieldCustom
                  label={t("E-mail")}
                  value={formData.email || '' }
                  errorText={t("")}
                  props={{
                    sx:{
                      '& .MuiOutlinedInput-input': {
                        textTransform: 'lowercase'
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={5} md={6}>
                <TextFieldCustom
                  label={t("Telefono")}
                  regex={/^\+?[0-9\s\-()]{7,20}$/}
                  value={formData.telephone?.toString() || '' }
                  onChange={(value) => handleChange(value, 'telephone')}
                  errorText={t("Richiesti solo Numeri")}
                  disable={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextFieldCustom
                  label={t("Professione")}
                  regex={/^[A-Za-zÀ-ÖØ-öø-ÿ][A-Za-zÀ-ÖØ-öø-ÿ'\- ]*$/}
                  value={formData.professione || ''}
                  onChange={(value) => handleChange(value, 'professione')}
                  errorText={t("Richieste lettere e spazi")}
                  disable={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextFieldCustom
                  label={t("Associazione di appartenenza")}
                  value={formData.association || '' }
                  errorText={t("")}
                />
              </Grid>
            </Grid>
          </Box>
        </form>
        
        <Grid container spacing={2} sx={{ marginTop: '30px', justifyContent: 'space-around'}}>
          <Grid item xs={12} md={5}>
            {!isEditing ?
              (
                <Typography sx={{
                  padding:'16px',
                  color: GREEN_MAIN,
                  fontSize:'0.9rem'
                }}>
                  {t(getAssociationStatusLabel(formData.approvedFromAssociation)) }
                </Typography>
            ):(
              <AssociationApproval 
                approvedFromAssociation={ formData.approvedFromAssociation ? formData.approvedFromAssociation : 0 }
                approvedFromAssociationReset={ initialFormData.approvedFromAssociation ? initialFormData.approvedFromAssociation : 0 }
                handleChange={handleChange}
              />
            )}
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ marginTop: '10px', justifyContent: 'space-around'}}>
          <Grid item xs={12} md={5}>
            {!isEditing ? (
                <Typography sx={{
                  padding:'16px',
                  color: GREEN_MAIN,
                  fontSize:'0.9rem'
                }}>
                  {formData.enabledUser ? t("Utente Abilitato") : t("Utente Disabilitato")}
                </Typography>
              ) : (
                <EnableUser 
                  approved={formData.enabledUser}
                  resetValue={initialFormData.enabledUser}
                  handleChange={handleChange}
                />
              )
            }
          </Grid>
        </Grid>
        {(canManageNationalEntity && (isEditing || formData.isNationalReference === true)) && (
        <Grid container spacing={2} sx={{ marginTop: '10px', justifyContent: 'space-around'}}>
          <Grid item xs={12} md={5}>
            {!isEditing ? (
                <Typography sx={{
                  padding:'16px',
                  color: GREEN_MAIN,
                  fontSize:'0.9rem',
                  fontWeight: 'bold',
                }}>
                  {t("Entità Nazionale")}
                </Typography>
              ) : (
                <NationalEntityCheckbox
                  checked={formData.isNationalReference === true}
                  handleChange={handleChange}
                />
              )
            }
          </Grid>
        </Grid>
        )}

        {!isEditing ? (
          <Grid container justifyContent="center" alignItems="center" spacing={3} sx={{ marginTop: '30px' }}>
            <Grid item xs={12} sm={4} md={3}>
              <Button variant="contained" onClick={handleEdit} sx={{
                margin:"10px 0 0 0",
                height: '35px',
                color: WHITE,
                backgroundColor: GREEN_LOGO,
                width: '100%',
                '&:hover': {
                  backgroundColor: GREEN_LOGO
                }
              }}>
                {t("Modifica")}
              </Button>
            </Grid>
          </Grid>
        ) : (
          <Grid container justifyContent="center" alignItems="center" spacing={1} sx={{ marginTop: '30px' }}>
            <Grid item xs={12} sm={4} md={3}>
              <Button variant="outlined" onClick={handleCancel} sx={{
                margin:"10px 0 0 0",
                height: '35px',
                width: '100%'
              }}>
                {t("Annulla")}
              </Button>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Button variant="contained" onClick={handleOpenModal} sx={{
                margin:"10px 0 0 0",
                height: '35px',
                color: WHITE,
                backgroundColor: GREEN_LOGO,
                width: '100%',
                '&:hover': {
                  backgroundColor: GREEN_LOGO
                }
              }}>
                {t("Salva")}
              </Button>
            </Grid>
          </Grid>
        )}


        {mainError && <>
            <Typography
              variant="body2" // Tipo di testo
              color="error"   // Colore rosso per il messaggio di errore
              sx={{
                maxHeight: '6.5em',        // Limite massimo di altezza per circa 5 righe (1.3em per linea)
                overflow: 'hidden',        // Nasconde l'overflow
                textOverflow: 'ellipsis',  // Aggiunge i puntini di sospensione se il testo è troppo lungo
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 5,        // Limita il numero massimo di righe a 5
                lineHeight: '1.3em',       // Altezza della riga per calcolare il numero di righe
                wordWrap: 'break-word',    // Va a capo automaticamente
                paddingTop:'20px'
              }}
            >
            {mainErrorMex}
          </Typography>
        </>
        }
      </div>

      <AlertSaveModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
      />

      {/* Popup che appare dopo il salvataggio */}
      <Snackbar open={showPopup} anchorOrigin={{vertical: 'bottom', horizontal: "right" }}>
        <Alert severity="success"
          sx={{ 
            border: '2px solid green',
            marginBottom:'30px'
          }}
          onClose={() => setShowPopup(false)}>
          {t("Salvataggio dati avvenuto con successo")}
        </Alert>
      </Snackbar>
    </>
  );
};


export default AdminPrivateUser;
